import { BaseStore } from "@Front/Stores/BaseStore";
import { getSiteTextFallbackDef, getSiteTextFallbackMap } from "@Front/Stores/defaultSiteTextFallbacks";
import { normalizeSiteTextLocale } from "@Front/Stores/siteTextLocale";

import SiteTextsApi, { type ISiteTexts, FetchSiteTextsParams, SiteTextDto } from "@Front/Api/LeCoffreApi/Public/SiteTexts/SiteTexts";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { is429Error } from "@Front/Utils/errorClassifiers";

export type SiteTextEntry = {
	textKey: string;
	scope: string;
	locale: string;
	content: string;
	isHtml: boolean;
	version: number;
	updatedAt: string | null;
};

type CompositeKey = `${string}::${string}`;

class SiteTextsStore extends BaseStore {
	private static _instance: SiteTextsStore | null = null;

	public static get instance(): SiteTextsStore {
		if (!SiteTextsStore._instance) {
			SiteTextsStore._instance = new SiteTextsStore();
		}
		return SiteTextsStore._instance;
	}

	private _siteTextsApi: ISiteTexts | null = null;

	private get siteTextsApi(): ISiteTexts {
		if (!this._siteTextsApi) {
			this._siteTextsApi = SiteTextsApi.getInstance();
		}
		return this._siteTextsApi;
	}
	private readonly entries: Map<CompositeKey, SiteTextEntry> = new Map();
	private readonly scopeCache: Map<string, Set<string>> = new Map();
	// Prevent duplicate concurrent loads and unnecessary bursts
	private readonly inflightLoads: Map<string, Promise<void>> = new Map();
	private readonly lastSuccessfulLoadAt: Map<string, number> = new Map();

	private makeCompositeKey(textKey: string, locale: string): CompositeKey {
		return `${textKey}::${locale}` as CompositeKey;
	}

	private makeLoadKey(params: FetchSiteTextsParams): string {
		const locale = normalizeSiteTextLocale(params.locale ?? "fr-FR");
		const scopes = (params.scopes ?? []).slice().sort().join(",");
		const keys = (params.textKeys ?? []).slice().sort().join(",");
		return [locale, scopes, keys].join("|");
	}

	public async preload(params: FetchSiteTextsParams): Promise<void> {
		const locale = normalizeSiteTextLocale(params.locale ?? "fr-FR");
		const loadKey = this.makeLoadKey(params);

		const existing = this.inflightLoads.get(loadKey);
		if (existing) {
			return existing;
		}

		const now = Date.now();
		const lastAt = this.lastSuccessfulLoadAt.get(loadKey) ?? 0;
		if (now - lastAt < 5_000) {
			return Promise.resolve();
		}

		const RETRY_DELAY_MS = 2000;
		const task = this.runPreloadTask(params, locale, loadKey, RETRY_DELAY_MS);

		this.inflightLoads.set(loadKey, task);
		return task;
	}

	private commitFetchedSiteTexts(
		params: FetchSiteTextsParams,
		locale: string,
		loadKey: string,
		texts: SiteTextDto[],
	): void {
		this.persistEntries(texts);
		this.applyFallbackEntriesForLocale(locale);
		this.registerScopeCache(params.scopes ?? [], texts, locale);
		this.lastSuccessfulLoadAt.set(loadKey, Date.now());
		this.event.emit("change", null);
	}

	private runPreloadTask(
		params: FetchSiteTextsParams,
		locale: string,
		loadKey: string,
		retryDelayMs: number,
	): Promise<void> {
		return (async (): Promise<void> => {
			try {
				const response = await this.siteTextsApi.fetchPublished({ ...params, locale });
				this.commitFetchedSiteTexts(params, locale, loadKey, response.texts);
			} catch (error) {
				if (is429Error(error)) {
					LoggerService.warn("⚠️ [SiteTextsStore] Rate limit (429), retrying once after delay", {
						loadKey,
						delayMs: retryDelayMs,
					});
					await new Promise((r) => setTimeout(r, retryDelayMs));
					const retryResponse = await this.siteTextsApi.fetchPublished({ ...params, locale });
					this.commitFetchedSiteTexts(params, locale, loadKey, retryResponse.texts);
					return;
				}
				LoggerService.error("❌ [SiteTextsStore] Failed to preload site texts", { error, params });
				throw error;
			} finally {
				this.inflightLoads.delete(loadKey);
			}
		})();
	}

	public getContent(textKey: string, locale: string = "fr-FR"): string {
		const entry = this.get(textKey, locale);
		return entry.content;
	}

	public get(textKey: string, locale: string = "fr-FR"): SiteTextEntry {
		const canonicalLocale = normalizeSiteTextLocale(locale);
		const composite = this.makeCompositeKey(textKey, canonicalLocale);
		const entry = this.entries.get(composite);

		if (entry) {
			return entry;
		}

		const fallbackDef = getSiteTextFallbackDef(textKey, canonicalLocale);
		if (fallbackDef !== null) {
			return {
				textKey: fallbackDef.textKey,
				scope: fallbackDef.scope,
				locale: canonicalLocale,
				content: fallbackDef.content,
				isHtml: Boolean(fallbackDef.isHtml),
				version: 0,
				updatedAt: null,
			};
		}

		LoggerService.error(`🚫 [SiteTextsStore] Missing site text | key=${textKey} | locale=${locale}`, {
			textKey,
			locale,
			canonicalLocale,
		});
		return {
			textKey,
			scope: textKey.split(".")[0] || "global",
			locale: canonicalLocale,
			content: "",
			isHtml: false,
			version: 0,
			updatedAt: null,
		};
	}

	public has(textKey: string, locale: string = "fr-FR"): boolean {
		const canonicalLocale = normalizeSiteTextLocale(locale);
		const composite = this.makeCompositeKey(textKey, canonicalLocale);
		return this.entries.has(composite);
	}

	private persistEntries(texts: SiteTextDto[]): void {
		for (const text of texts) {
			const locale = normalizeSiteTextLocale(text.locale);
			const composite = this.makeCompositeKey(text.text_key, locale);
			this.entries.set(composite, {
				textKey: text.text_key,
				scope: text.scope,
				locale,
				content: text.content,
				isHtml: text.is_html,
				version: text.version,
				updatedAt: text.updated_at,
			});
		}
	}

	/** Remplit uniquement les clés absentes après chargement API (contenu publié prioritaire). */
	private applyFallbackEntriesForLocale(locale: string): void {
		const canonicalLocale = normalizeSiteTextLocale(locale);
		const fallbacks = getSiteTextFallbackMap(canonicalLocale);
		if (!fallbacks) {
			return;
		}
		for (const [textKey, tuple] of Object.entries(fallbacks)) {
			if (this.has(textKey, canonicalLocale)) {
				continue;
			}
			const [scope, content] = tuple;
			const composite = this.makeCompositeKey(textKey, canonicalLocale);
			this.entries.set(composite, {
				textKey,
				scope,
				locale: canonicalLocale,
				content,
				isHtml: false,
				version: 0,
				updatedAt: null,
			});
		}
	}

	private registerScopeCache(scopes: string[], texts: SiteTextDto[], locale: string): void {
		if (scopes.length === 0) {
			return;
		}

		for (const scope of scopes) {
			const cacheKey = `${scope}::${locale}`;
			const existing = this.scopeCache.get(cacheKey) ?? new Set<string>();
			for (const text of texts) {
				if (text.scope === scope) {
					existing.add(text.text_key);
				}
			}
			this.scopeCache.set(cacheKey, existing);
		}
	}
}

// Export named only - no default export to avoid Webpack initialization order issues
export { SiteTextsStore };
